import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { CText, CHeader } from '../../components/common';
import { FiVideo, FiMessageSquare, FiCheck, FiX, FiInfo } from 'react-icons/fi';
import axiosClient from '../../api/axios';
import { setUser } from '../../store/slices/userSlice';

const SessionRates: React.FC = () => {
  const dispatch = useAppDispatch();
  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);

  const [callRate, setCallRate] = useState(user?.call_session_rate?.toString() || '500');
  const [chatRate, setChatRate] = useState(user?.chat_session_rate?.toString() || '300');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (user) {
      setCallRate(user.call_session_rate?.toString() || '500');
      setChatRate(user.chat_session_rate?.toString() || '300');
    }
  }, [user]);

  const handleSave = async () => {
    const callRateNum = parseInt(callRate);
    const chatRateNum = parseInt(chatRate);

    if (isNaN(callRateNum) || callRateNum < 100) {
      setMessage({ type: 'error', text: 'Video call rate must be at least Rs.100' });
      return;
    }
    if (isNaN(chatRateNum) || chatRateNum < 50) {
      setMessage({ type: 'error', text: 'Chat rate must be at least Rs.50' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      await axiosClient.post('auth/user_details/', {
        call_session_rate: callRateNum,
        chat_session_rate: chatRateNum,
      });

      // Update Redux store
      if (user) {
        dispatch(setUser({
          ...user,
          call_session_rate: callRateNum,
          chat_session_rate: chatRateNum,
        }));
      }

      setMessage({ type: 'success', text: 'Rates updated successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update rates' });
    } finally {
      setSaving(false);
    }
  };

  const calculateEarnings = (rate: number) => {
    // Platform fee: 20%
    const platformFee = rate * 0.2;
    const netEarnings = rate - platformFee;
    return { platformFee, netEarnings };
  };

  const callEarnings = calculateEarnings(parseInt(callRate) || 0);
  const chatEarnings = calculateEarnings(parseInt(chatRate) || 0);

  return (
    <div
      className="min-h-screen pb-8"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="p-4 lg:p-8">
        <div className="lg:max-w-2xl lg:mx-auto">
          <CHeader title="Session Rates" />

          {/* Info Banner */}
          <div
            className="mb-6 p-4 rounded-xl flex items-start gap-3"
            style={{
              backgroundColor: colors.primary + '10',
              border: `1px solid ${colors.primary}30`,
            }}
          >
            {React.createElement(FiInfo as any, { size: 20, color: colors.primary, className: 'flex-shrink-0 mt-0.5' })}
            <div>
              <CText type="M14" color={colors.primary}>
                Pricing Guidelines
              </CText>
              <CText type="r12" color={colors.grayScale500} style={{ marginTop: 4 }}>
                Set competitive rates to attract more clients. All rates are per 30-minute session.
                Platform fee is 20% of each session.
              </CText>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div
              className="mb-4 p-3 rounded-xl flex items-center gap-2"
              style={{
                backgroundColor: message.type === 'success' ? colors.green + '20' : colors.alertColor + '20',
              }}
            >
              {React.createElement(
                message.type === 'success' ? FiCheck : FiX as any,
                { size: 18, color: message.type === 'success' ? colors.green : colors.alertColor }
              )}
              <CText
                type="r14"
                color={message.type === 'success' ? colors.green : colors.alertColor}
              >
                {message.text}
              </CText>
            </div>
          )}

          {/* Video Call Rate */}
          <div
            className="rounded-2xl p-5 mb-4"
            style={{
              backgroundColor: colors.cardBackground || colors.inputBackground,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: colors.primary + '15' }}
              >
                {React.createElement(FiVideo as any, { size: 22, color: colors.primary })}
              </div>
              <div>
                <CText type="B16" color={colors.textColor}>
                  Video Call Rate
                </CText>
                <CText type="r12" color={colors.grayScale500}>
                  Per 30-minute video session
                </CText>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <span style={{ color: colors.textColor, fontSize: 18 }}>Rs.</span>
              <input
                type="number"
                value={callRate}
                onChange={(e) => setCallRate(e.target.value)}
                min="100"
                step="50"
                className="flex-1 px-4 py-3 rounded-xl text-xl font-bold outline-none"
                style={{
                  backgroundColor: colors.backgroundColor,
                  color: colors.textColor,
                  border: `1px solid ${colors.borderColor}`,
                }}
              />
            </div>

            <div className="flex justify-between items-center p-3 rounded-lg" style={{ backgroundColor: colors.backgroundColor }}>
              <CText type="r12" color={colors.grayScale500}>
                Platform fee (20%)
              </CText>
              <CText type="r12" color={colors.alertColor}>
                -Rs.{callEarnings.platformFee.toFixed(0)}
              </CText>
            </div>
            <div className="flex justify-between items-center p-3 mt-1 rounded-lg" style={{ backgroundColor: colors.green + '10' }}>
              <CText type="M12" color={colors.green}>
                Your earnings
              </CText>
              <CText type="B14" color={colors.green}>
                Rs.{callEarnings.netEarnings.toFixed(0)}
              </CText>
            </div>
          </div>

          {/* Chat Rate */}
          <div
            className="rounded-2xl p-5 mb-6"
            style={{
              backgroundColor: colors.cardBackground || colors.inputBackground,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: colors.primary + '15' }}
              >
                {React.createElement(FiMessageSquare as any, { size: 22, color: colors.primary })}
              </div>
              <div>
                <CText type="B16" color={colors.textColor}>
                  Chat Session Rate
                </CText>
                <CText type="r12" color={colors.grayScale500}>
                  Per 30-minute chat session
                </CText>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <span style={{ color: colors.textColor, fontSize: 18 }}>Rs.</span>
              <input
                type="number"
                value={chatRate}
                onChange={(e) => setChatRate(e.target.value)}
                min="50"
                step="50"
                className="flex-1 px-4 py-3 rounded-xl text-xl font-bold outline-none"
                style={{
                  backgroundColor: colors.backgroundColor,
                  color: colors.textColor,
                  border: `1px solid ${colors.borderColor}`,
                }}
              />
            </div>

            <div className="flex justify-between items-center p-3 rounded-lg" style={{ backgroundColor: colors.backgroundColor }}>
              <CText type="r12" color={colors.grayScale500}>
                Platform fee (20%)
              </CText>
              <CText type="r12" color={colors.alertColor}>
                -Rs.{chatEarnings.platformFee.toFixed(0)}
              </CText>
            </div>
            <div className="flex justify-between items-center p-3 mt-1 rounded-lg" style={{ backgroundColor: colors.green + '10' }}>
              <CText type="M12" color={colors.green}>
                Your earnings
              </CText>
              <CText type="B14" color={colors.green}>
                Rs.{chatEarnings.netEarnings.toFixed(0)}
              </CText>
            </div>
          </div>

          {/* Suggested Rates */}
          <div
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.backgroundColor }}
          >
            <CText type="M12" color={colors.grayScale500} style={{ marginBottom: 8 }}>
              Suggested rates based on experience:
            </CText>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded-lg" style={{ backgroundColor: colors.inputBackground }}>
                <CText type="r10" color={colors.grayScale500}>
                  0-3 years
                </CText>
                <CText type="M12" color={colors.textColor}>
                  Rs.300-500
                </CText>
              </div>
              <div className="p-2 rounded-lg" style={{ backgroundColor: colors.inputBackground }}>
                <CText type="r10" color={colors.grayScale500}>
                  3-7 years
                </CText>
                <CText type="M12" color={colors.textColor}>
                  Rs.500-800
                </CText>
              </div>
              <div className="p-2 rounded-lg" style={{ backgroundColor: colors.inputBackground }}>
                <CText type="r10" color={colors.grayScale500}>
                  7+ years
                </CText>
                <CText type="M12" color={colors.textColor}>
                  Rs.800-1500
                </CText>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-4 rounded-2xl font-semibold transition-all"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
              color: '#fff',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save Rates'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionRates;
